import React, { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { LectureDownloader } from '@/components/LectureDownloader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ArrowLeft, ArrowRight, Download, File } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { useToast } from "@/hooks/use-toast";

interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

/**
 * NAC Clinical Application Lecture Component
 */
const NACClinicalApplication: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { toast } = useToast();

  const handlePrevious = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNext = () => {
    if (currentSlide < lectureSlides.length) {
      setCurrentSlide(currentSlide + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleDownload = () => {
    toast({
      title: "Download started",
      description: "Your slides are being prepared for download.",
    });
    // In a real implementation, this would initiate a download
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>NAC Clinical Application</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex justify-between items-center mb-6">
          <Link href="/mentorship">
            <Button 
              variant="ghost" 
              className="flex items-center text-[#3498DB]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </Link>
          
          <div className="flex items-center gap-4">
            <LectureDownloader lectureName="NAC Clinical Application" fileName="NACClinicalApplication" />
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              Lecture Material
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2">
            <Card className="mb-8">
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold mb-4">{lectureSlides[currentSlide - 1].title}</h1>
                <div className="prose max-w-none">
                  {lectureSlides[currentSlide - 1].content}
                </div>
              </CardContent>
            </Card>
            
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button 
                    variant="outline" 
                    onClick={handlePrevious}
                    disabled={currentSlide === 1}
                    className="flex items-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>
                </PaginationItem>
                
                <PaginationItem className="flex items-center px-4">
                  <span className="text-sm text-gray-500">
                    Slide {currentSlide} of {lectureSlides.length}
                  </span>
                </PaginationItem>
                
                <PaginationItem>
                  <Button 
                    variant="outline" 
                    onClick={handleNext}
                    disabled={currentSlide === lectureSlides.length}
                    className="flex items-center"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            
            {/* Video Embed */}
            <div className="mt-8">
              <h3 className="text-lg font-medium mb-4">Lecture Video</h3>
              <div className="flex justify-center">
                <iframe 
                  src="https://share.descript.com/embed/a96UlhAMqcP" 
                  width="640" 
                  height="360" 
                  frameBorder="0" 
                  allowFullScreen
                  className="rounded-lg shadow-sm"
                  title="NAC Clinical Application - Lecture Video"
                />
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-3">Lecture Resources</h3>
                <div className="space-y-4">
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-start"
                    onClick={handleDownload}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Slides
                  </Button>
                  <Link href="/mentorship/qa">
                    <Button variant="outline" className="w-full flex items-center justify-start">
                      <File className="h-4 w-4 mr-2" />
                      Related Q&A
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-3">Navigation</h3>
                <div className="space-y-2 text-sm">
                  {lectureSlides.map((slide) => (
                    <div 
                      key={slide.id}
                      className={`p-2 rounded cursor-pointer ${
                        currentSlide === slide.id 
                          ? "bg-blue-100 text-blue-800 font-medium" 
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        setCurrentSlide(slide.id);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {slide.id}. {slide.title}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

// Lecture slides data
const lectureSlides: Slide[] = [
  {
    id: 1,
    title: "Clinical Application of N-Acetylcysteine (NAC) in Longevity Medicine",
    content: (
      <>
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-xl font-bold text-center mb-2">Clinical Application of N-Acetylcysteine (NAC) in Longevity Medicine and Chronic Disease Prevention</h2>
          <p className="text-center text-gray-600 italic mb-4">
            Equipping Clinicians with Mechanistic Insights, Protocols, and Practical Implementation Strategies
          </p>
          <div className="mt-4 py-4 px-6 bg-blue-50 rounded-lg">
            <p className="text-center text-sm text-gray-600">
              Next Generation Medicine | Mentorship Program<br />
              Lecture Date: May 7, 2025
            </p>
          </div>
        </div>
        <div className="w-full h-64 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center mb-4">
          <div className="w-20 h-20 text-indigo-500">
            {/* This would be where the molecular structure image would go */}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" />
              <path d="M12 5V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 15V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M5 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M15 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 2,
    title: "Welcome: Unveiling the Potential of NAC",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Welcome: Unveiling the Potential of NAC</h3>
        
        <div className="mb-6">
          <p className="font-serif text-lg italic mb-4">
            "Today, we explore N-acetylcysteine (NAC) – a versatile compound with significant applications in longevity medicine and chronic disease prevention."
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="font-bold mb-2">Our Goal: To provide a clear understanding of:</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>NAC's mechanisms of action</li>
            <li>Practical dosing and administration</li>
            <li>Synergistic therapeutic combinations</li>
            <li>Key biomarkers for monitoring efficacy</li>
            <li>Patient selection, safety, and clinical pearls</li>
          </ul>
        </div>
        
        <p className="font-serif text-lg italic">
          "Let's dive into how NAC can become a valuable tool in your clinical practice."
        </p>
      </>
    ),
  },
  {
    id: 3,
    title: "N-Acetylcysteine (NAC): A Glutathione Precursor and More",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">N-Acetylcysteine (NAC): A Glutathione Precursor and More</h3>
        
        <div className="mb-6">
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Derivative of the amino acid L-cysteine.</li>
            <li>Well-absorbed acetylated form; releases cysteine intracellularly.</li>
            <li><strong>Primary Role:</strong> Serves as a precursor to Glutathione (GSH) – the body's master antioxidant.</li>
            <li>Cysteine is the rate-limiting step for GSH synthesis.</li>
            <li>NAC effectively boosts intracellular GSH.</li>
            <li><strong>Key Clinical Impact:</strong> Replenishes GSH stores, reduces oxidative stress, protects cells from damage – crucial in aging and chronic disease.</li>
          </ul>
        </div>
        
        <div className="w-full bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3 text-center">GSH Synthesis Pathway</h4>
          <div className="w-full flex flex-col items-center">
            <div className="flex items-center justify-center w-full">
              <div className="border border-blue-300 rounded px-4 py-2 bg-white">NAC (Oral Intake)</div>
              <div className="w-8 h-0.5 bg-blue-400"></div>
              <div className="border border-blue-300 rounded px-4 py-2 bg-white">Deacetylation in cell</div>
              <div className="w-8 h-0.5 bg-blue-400"></div>
              <div className="border border-blue-300 rounded px-4 py-2 bg-white">L-Cysteine</div>
            </div>
            <div className="h-8 border-l border-blue-400 mx-auto"></div>
            <div className="text-center text-sm text-blue-600 px-2 py-1 bg-blue-100 rounded mb-2">Rate-Limiting Step</div>
            <div className="h-4 border-l border-blue-400 mx-auto"></div>
            <div className="border border-blue-300 rounded px-4 py-2 bg-white font-bold text-blue-700">Glutathione (GSH)</div>
            <div className="h-8 border-l border-blue-400 mx-auto"></div>
            <div className="border border-blue-300 rounded-lg px-4 py-2 bg-white">Cellular Protection / Antioxidant Defense</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 4,
    title: "Beyond GSH: Diverse Mechanisms of NAC",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Beyond GSH: Diverse Mechanisms of NAC</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
              </div>
            </div>
            <h4 className="font-semibold text-center mb-2">1. Direct Antioxidant</h4>
            <p className="text-sm">
              Free thiol (-SH) group directly scavenges Reactive Oxygen Species (ROS) and Reactive Nitrogen Species (RNS).
            </p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                  <path d="M6.38 6.38S8.4 9.05 12 9.05 17.62 6.38 17.62 6.38M12 4v1m-6.33 9.13l-1 1.74M19.33 14.13l1 1.74M10 15a2 2 0 100-4 2 2 0 000 4z" />
                  <path d="M18 15c-.5-1.5-2-3-6-3s-5.5 1.5-6 3" />
                </svg>
              </div>
            </div>
            <h4 className="font-semibold text-center mb-2">2. Mucolytic & Biofilm Disruptor</h4>
            <p className="text-sm">
              Breaks disulfide bonds in mucus (e.g., respiratory, GI tract). Disrupts pathogenic biofilms, aiding antimicrobial penetration and immune clearance.
            </p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z" />
                  <path d="M15.7 8.3l-6 6M8.3 8.3l6 6" />
                </svg>
              </div>
            </div>
            <h4 className="font-semibold text-center mb-2">3. Anti-inflammatory Effects</h4>
            <ul className="text-sm list-disc pl-4">
              <li>Reduces pro-inflammatory cytokines (TNF-α, IL-1β, IL-6)</li>
              <li>Inhibits NF-κB activation</li>
              <li>Modulates nitric oxide and eicosanoid pathways</li>
              <li>Potential to lower systemic inflammation (e.g., CRP)</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 5,
    title: "Protecting Powerhouses & Facilitating Detox",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Protecting Powerhouses & Facilitating Detox</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-3">4. Mitochondrial Protection</h4>
            
            <ul className="list-disc pl-5 mb-4 text-sm">
              <li>Maintains mitochondrial glutathione (mtGSH), quenching ROS at the source</li>
              <li>Preserves mitochondrial membrane integrity and function</li>
              <li>Restores mitochondrial complex activity and ATP production</li>
              <li>GlyNAC (Glycine + NAC) Example: Improved mitochondrial fatty acid oxidation in older adults</li>
            </ul>
            
            <div className="bg-white p-3 rounded border border-purple-200">
              <h5 className="text-sm font-medium text-center mb-2">Mitochondrial Function</h5>
              <div className="flex flex-col items-center">
                <div className="px-3 py-1 border border-purple-300 rounded bg-purple-50 text-sm">NAC</div>
                <div className="h-4 w-0.5 bg-purple-300"></div>
                <div className="px-3 py-1 border border-purple-300 rounded bg-purple-50 text-sm">mtGSH</div>
                <div className="h-4 w-0.5 bg-purple-300"></div>
                <div className="px-3 py-1 border border-purple-300 rounded bg-purple-50 text-sm">Reduced ROS</div>
                <div className="h-4 w-0.5 bg-purple-300"></div>
                <div className="px-3 py-1 border border-purple-300 rounded bg-purple-50 text-sm font-medium">ATP Production</div>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-3">5. Liver Protection & Detoxification</h4>
            
            <ul className="list-disc pl-5 mb-4 text-sm">
              <li>Life-saving in acetaminophen overdose (restores hepatic GSH)</li>
              <li>Enhances Phase II detoxification (glutathione conjugation)</li>
              <li>Improves liver function tests (e.g., ALT) in NAFLD</li>
            </ul>
            
            <div className="bg-white p-3 rounded border border-yellow-200">
              <h5 className="text-sm font-medium text-center mb-2">Liver Function</h5>
              <div className="flex justify-center items-center">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-yellow-600">
                    <path d="M50 5 C 25 5, 5 30, 5 50 C 5 75, 30 95, 50 95 C 80 95, 95 75, 95 50 C 95 25, 75 5, 50 5" fill="#FEF9C3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-xs text-center space-y-1">
                      <div>NAC → GSH</div>
                      <div>↓</div>
                      <div>Detoxification</div>
                      <div>↓</div>
                      <div>Reduced ALT</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 6,
    title: "NAC: Brain Health and Healthy Aging",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">NAC: Brain Health and Healthy Aging</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-indigo-50 p-4 rounded-lg">
            <div className="flex justify-center mb-3">
              <div className="w-10 h-10">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-indigo-600">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="currentColor" />
                </svg>
              </div>
            </div>
            <h4 className="font-semibold text-center mb-2">Neurological Benefits</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Crosses blood-brain barrier (modestly), raises brain GSH</li>
              <li>Parkinson's Disease: IV + oral NAC improved dopamine neuron integrity & motor scores</li>
              <li>Alzheimer's Models: Protected neurons from amyloid-induced toxicity</li>
            </ul>
          </div>
          
          <div className="bg-teal-50 p-4 rounded-lg">
            <div className="flex justify-center mb-3">
              <div className="w-10 h-10">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-teal-600">
                  <path d="M12 8V12L14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M2.45 14.5C3.5 19 7.5 22 12 22C16.5 22 20.5 19 21.55 14.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M21.55 9.5C20.5 5 16.5 2 12 2C7.5 2 3.5 5 2.45 9.5" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
            </div>
            <h4 className="font-semibold text-center mb-2">Longevity & Geroscience</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Extends lifespan in model organisms (e.g., C. elegans by &gt;30%)</li>
              <li>Reduces age-related metabolic and cognitive declines in rodents</li>
              <li>Cellular Senescence: Reduced markers (SA-β-gal, p16INK4A) in obese adults</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-center mb-2">Summary of Mechanisms</h4>
          <p className="text-sm">
            NAC acts via GSH boosting, direct antioxidation, mitochondrial support, detox, anti-inflammation, and cellular aging modulation.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 7,
    title: "Oral NAC: The Mainstay for Longevity & Prevention",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Oral NAC: The Mainstay for Longevity & Prevention</h3>
        
        <div className="mb-5">
          <p className="mb-2"><strong>Bioavailability:</strong> ~9-10% (first-pass metabolism), sufficient for systemic effects.</p>
        </div>
        
        <div className="space-y-5">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Maintenance Dosing:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>600 mg to 1,200 mg per day (often split 1-2 doses)</li>
              <li>Significant effects on oxidative stress/inflammation even at 600 mg/day</li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Therapeutic Dosing (Chronic Conditions):</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>1,800 mg to 3,000 mg per day (divided doses of 600-1,000 mg)</li>
              <li>Used in trials for OCD, autism, NAFLD</li>
              <li>Functional medicine: Up to 4,000-6,000 mg/day (short-term, intensive)</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Titration Strategy:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Start: 500-600 mg daily for 1 week</li>
              <li>Increase: By 500-600 mg every 1-2 weeks as tolerated/needed</li>
            </ul>
            <div className="mt-3 p-2 bg-yellow-100 rounded text-center font-medium">
              Start Low, Titrate Up
            </div>
          </div>
          
          <div className="p-4 rounded-lg border border-gray-200">
            <h4 className="font-semibold mb-2">Tips:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Take away from protein-rich meals</li>
              <li>Capsules preferred (taste)</li>
              <li>Good hydration</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 8,
    title: "Other NAC Routes & Dosing Overview",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Other NAC Routes & Dosing Overview</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">Intravenous (IV) NAC</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Bypasses GI, higher plasma levels</li>
              <li>Acute liver toxicity (150 mg/kg loading)</li>
              <li>Integrative use: 1-2g infusions</li>
              <li>Caution: Infuse slowly (risk of allergic reaction)</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">Rectal (Suppository/Enema)</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Less common</li>
              <li>For oral intolerance or direct gut/portal delivery</li>
              <li>500-1000mg suppository</li>
              <li>Experimental in colitis</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">Inhalation (Nebulized)</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Primarily for respiratory (mucolytic)</li>
              <li>3-5 mL of 10-20% NAC solution (e.g., 600mg)</li>
              <li>Caution: Bronchospasm</li>
            </ul>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Route</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Typical Dose Range (Longevity/Chronic)</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Key Use Cases</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-blue-50 font-medium">
                <td className="px-4 py-2 text-sm border-b">Oral</td>
                <td className="px-4 py-2 text-sm border-b">
                  Maintenance: 600-1,200 mg/day<br />
                  Therapeutic: 1,800-3,000+ mg/day
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Daily supplementation, prevention, NAFLD, mood disorders
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Mainstay. Start low. Split doses.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm border-b">IV</td>
                <td className="px-4 py-2 text-sm border-b">
                  1-2g (integrative)<br />
                  150mg/kg (acute APAP)
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Acute need, neurodegenerative
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Monitor for reactions, specialized setting
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm border-b">Nebulized</td>
                <td className="px-4 py-2 text-sm border-b">
                  600mg (3-5mL of 10-20% solution)
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Respiratory conditions
                </td>
                <td className="px-4 py-2 text-sm border-b">
                  Monitor for bronchospasm
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: 9,
    title: "Amplifying NAC's Benefits: Key Combinations",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Amplifying NAC's Benefits: Key Combinations</h3>
        
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">1. NAC + Glycine (GlyNAC): The Anti-Aging Power Duo</h4>
            <ul className="list-disc pl-6 text-sm space-y-1 mb-3">
              <li>Glutathione = Glutamate + Cysteine (from NAC) + Glycine</li>
              <li>Glycine often limiting in older individuals</li>
              <li>GlyNAC (e.g., 100mg/kg/day each) benefits:
                <ul className="list-disc pl-6 mt-1">
                  <li>Restores GSH, reduces oxidative stress</li>
                  <li>Improves mitochondrial function, insulin sensitivity</li>
                  <li>Decreases inflammation, improves muscle strength/gait speed</li>
                </ul>
              </li>
              <li>Clinical Approach: Add Glycine 1-3g BID with NAC</li>
            </ul>
            
            <div className="bg-white p-3 rounded border border-blue-200">
              <div className="flex items-center justify-center text-sm">
                <div className="px-3 py-1 bg-blue-100 rounded">NAC (Cysteine)</div>
                <div className="mx-1">+</div>
                <div className="px-3 py-1 bg-blue-100 rounded">Glycine</div>
                <div className="mx-2">&rArr;</div>
                <div className="px-3 py-1 bg-blue-100 rounded font-medium">MAX GSH Production</div>
                <div className="mx-2">&rArr;</div>
                <div className="px-3 py-1 bg-blue-100 rounded font-medium">Multiple Anti-Aging Benefits</div>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">2. NAC + B Vitamins (Methylation Cofactors: B6, B12, Folate)</h4>
            <ul className="list-disc pl-6 text-sm space-y-1 mb-3">
              <li>NAC can influence homocysteine (Hcy) metabolism (transsulfuration pathway)</li>
              <li>Adequate B vitamins needed to process Hcy, prevent accumulation</li>
              <li>Clinical Pearl: Ensure B-vitamin sufficiency, especially if Hcy is &gt;8 µmol/L. Consider activated B-complex.</li>
            </ul>
            
            <div className="bg-white p-3 rounded border border-green-200">
              <h5 className="text-xs text-center mb-2">Homocysteine Metabolism Pathways</h5>
              <div className="w-full flex justify-center">
                <div className="relative">
                  <div className="flex flex-col items-center">
                    <div className="bg-yellow-100 px-2 py-1 rounded text-xs">Methionine</div>
                    <div className="h-4 w-0.5 bg-gray-300"></div>
                    <div className="bg-gray-100 px-2 py-1 rounded text-xs">Homocysteine</div>
                    
                    <div className="absolute top-12 left-16 flex items-center">
                      <div className="h-0.5 w-10 bg-blue-300"></div>
                      <div className="bg-blue-100 px-2 py-1 rounded text-xs">B12, Folate</div>
                      <div className="ml-2 text-xs">Remethylation</div>
                    </div>
                    
                    <div className="absolute top-12 right-16 flex items-center">
                      <div className="bg-green-100 px-2 py-1 rounded text-xs">B6, NAC</div>
                      <div className="h-0.5 w-10 bg-green-300 ml-2"></div>
                      <div className="ml-2 text-xs">Transsulfuration</div>
                    </div>
                    
                    <div className="absolute top-24 left-16">
                      <div className="h-12 w-0.5 ml-2 bg-blue-300"></div>
                      <div className="bg-blue-100 px-2 py-1 rounded text-xs">Methionine</div>
                    </div>
                    
                    <div className="absolute top-24 right-16">
                      <div className="h-12 w-0.5 ml-2 bg-green-300"></div>
                      <div className="bg-green-100 px-2 py-1 rounded text-xs">Cysteine → GSH</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 10,
    title: "Further Synergies: Mitochondria, Antioxidants & Gut",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Further Synergies: Mitochondria, Antioxidants & Gut</h3>
        
        <div className="grid grid-cols-1 gap-5">
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-yellow-100 rounded-full text-yellow-700 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2zM2 12h.01M22 12h.01M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
              <h4 className="font-semibold">3. NAC + CoQ10 (Ubiquinol): Mitochondrial Rejuvenation</h4>
            </div>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>CoQ10: ATP production, membrane stability</li>
              <li>NAC: Provides mtGSH to scavenge ROS from energy production</li>
              <li>Use: Chronic fatigue, muscle weakness, cardiovascular health. (CoQ10: 100-300mg/day)</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full text-blue-700 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12h6M12 9v6M5 3v4M3 5h4M19 3v4M17 5h4M19 17v4M17 19h4M5 17v4M3 19h4M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                </svg>
              </div>
              <h4 className="font-semibold">4. NAC + Alpha-Lipoic Acid (ALA): Detox & Insulin Sensitivity</h4>
            </div>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>ALA: Raises GSH, broad antioxidant, regenerates other antioxidants, metal chelator</li>
              <li>Use: Detox protocols (heavy metals), diabetic neuropathy. (ALA: 300-600mg/day)</li>
              <li>Caution: Both sulfur-containing; start low if sensitivity suspected</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full text-green-700 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 12C16 8.69 13.31 6 10 6 7.19 6 4.85 7.86 4.19 10.5M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                </svg>
              </div>
              <h4 className="font-semibold">5. NAC + Mesalamine (5-ASA): Inflammatory Bowel Disease (IBD)</h4>
            </div>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Mesalamine: First-line for Ulcerative Colitis (UC)</li>
              <li>NAC complements by reducing mucosal oxidative stress</li>
              <li>Human Trial (UC): NAC (400-500mg BID) extended remission, lowered CRP/fecal calprotectin</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 11,
    title: "Customizing NAC Stacks",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Customizing NAC Stacks</h3>
        
        <div className="space-y-5">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">NAC + Glutathione (Liposomal/S-Acetyl):</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>For severe oxidative stress or GSH synthesis issues</li>
              <li>NAC builds endogenous GSH; supplemental GSH adds directly</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">NAC + Polyphenols/Other Antioxidants:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Vitamin C: Recycles GSH, direct antioxidant (500-1000mg+)</li>
              <li>Resveratrol: Activates Nrf2 pathway, complements NAC action</li>
              <li>Curcumin: Anti-inflammatory, supports GSH production</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">NAC + Melatonin:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Melatonin: Potent antioxidant, mitochondria-protective, sleep aid</li>
              <li>Potential for neuroinflammation, UC (daytime NAC, nighttime Melatonin)</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Tailoring Examples:</h4>
            <ul className="list-disc pl-6 text-sm space-y-1">
              <li>Liver Health: NAC + Milk Thistle (Silymarin)</li>
              <li>Mental Health: NAC + Selenium + Zinc</li>
            </ul>
            <p className="mt-2 text-sm font-medium">
              Key Principle: Tailor the stack to the patient's specific needs and biochemical imbalances.
            </p>
          </div>
        </div>
        
        <div className="mt-5 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h16" />
              <circle cx="9" cy="7" r="2" fill="currentColor" />
              <circle cx="15" cy="12" r="2" fill="currentColor" />
              <circle cx="8" cy="17" r="2" fill="currentColor" />
            </svg>
          </div>
          <p className="text-center text-sm mt-2">
            Customize NAC combinations to address specific patient needs
          </p>
        </div>
      </>
    ),
  },
  {
    id: 12,
    title: "Tracking Success: Essential Biomarkers for NAC Therapy",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Tracking Success: Essential Biomarkers for NAC Therapy</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">1. Glutathione Levels</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>RBC Glutathione or WBC Glutathione: Best reflects body stores</li>
              <li><strong>Expected:</strong> Increase in reduced GSH (GSH) or improved GSH:GSSG ratio</li>
            </ul>
            <div className="mt-2 flex justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8M8 12h8" />
              </svg>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">2. Oxidative Stress Markers</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>8-OHdG (DNA damage marker): <strong>Expected: Decrease</strong></li>
              <li>F2-Isoprostanes (Lipid peroxidation): <strong>Expected: Decrease</strong></li>
            </ul>
            <div className="mt-2 flex justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-semibold text-center mb-2">3. Inflammatory Markers</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>hs-CRP (systemic inflammation): <strong>Expected: Decrease</strong></li>
              <li>Fecal Calprotectin (intestinal inflammation): <strong>Expected: Decrease</strong></li>
            </ul>
            <div className="mt-2 flex justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2zM6 12h.01M6 16h.01" />
              </svg>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 13,
    title: "More Biomarkers & Summary Table",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">More Biomarkers & Summary Table</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">4. Liver Enzymes</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>ALT, AST, GGT: Hepatocellular injury/stress</li>
              <li><strong>Expected:</strong> Decrease (esp. ALT in NAFLD; GGT if initially high)</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">5. Metabolic Markers</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Fasting Insulin & HOMA-IR: Insulin resistance</li>
              <li><strong>Expected:</strong> Decrease (improved insulin sensitivity)</li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">6. Homocysteine (Plasma)</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Methylation status, CV risk</li>
              <li><strong>Expected:</strong> Variable; often decreases if initially high</li>
              <li>Monitor; add B vitamins if rises</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">7. Pyroglutamate (Urine Organic Acids)</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Indicator of glutathione depletion</li>
              <li><strong>Expected:</strong> Decrease (normalization)</li>
            </ul>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-b">Biomarker</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-b">Relevance</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-b">Expected Change with NAC</th>
              </tr>
            </thead>
            <tbody className="text-xs">
              <tr>
                <td className="px-4 py-2 border-b">RBC/WBC Glutathione</td>
                <td className="px-4 py-2 border-b">Master antioxidant stores</td>
                <td className="px-4 py-2 border-b text-green-600">Increase in GSH, Improved GSH:GSSG ratio</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-b">8-OHdG (Urine)</td>
                <td className="px-4 py-2 border-b">Oxidative DNA damage</td>
                <td className="px-4 py-2 border-b text-green-600">Decrease</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-b">hs-CRP</td>
                <td className="px-4 py-2 border-b">Systemic inflammation</td>
                <td className="px-4 py-2 border-b text-green-600">Decrease</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-b">ALT, AST</td>
                <td className="px-4 py-2 border-b">Liver cell stress</td>
                <td className="px-4 py-2 border-b text-green-600">Decrease (esp. ALT in NAFLD)</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-b">Fasting Insulin</td>
                <td className="px-4 py-2 border-b">Insulin resistance</td>
                <td className="px-4 py-2 border-b text-green-600">Decrease</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-b">Homocysteine</td>
                <td className="px-4 py-2 border-b">Methylation status</td>
                <td className="px-4 py-2 border-b text-blue-600">Variable (monitor)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: 14,
    title: "Who Benefits Most from NAC Therapy?",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Who Benefits Most from NAC Therapy?</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Middle-aged & Older Adults
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Signs of oxidative stress, chronic inflammation, age-related GSH decline</li>
              <li>Preventive antioxidant support, slowing cellular aging</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Chronic Liver Burden
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>NAFLD, history of hepatotoxic exposures, elevated liver enzymes</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Detoxification Support
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Heavy metals, mold toxins, environmental toxin clearance</li>
              <li>Smokers</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Chronic Respiratory Conditions
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>COPD, chronic bronchitis, chronic sinusitis (mucolytic & antioxidant)</li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Mental Health & Neurological Conditions
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>OCD, addictions, bipolar depression, autism (glutamate modulation, brain GSH)</li>
              <li>Parkinson's, Alzheimer's (neuroprotection)</li>
            </ul>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Gut Health & Autoimmune Conditions
            </h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>IBD (UC, Crohn's), IBS with biofilm suspicion</li>
              <li>RA, Lupus, MS (reduce oxidative stress, support immune modulation)</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 15,
    title: "NAC: Important Cautions & Considerations",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">NAC: Important Cautions & Considerations</h3>
        
        <div className="mb-6 bg-red-100 border-l-4 border-red-500 p-4 rounded-lg">
          <h4 className="font-bold text-red-700 mb-1 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            CRITICAL CAUTION: Active Cancer or Precancerous Lesions
          </h4>
          <ul className="text-sm list-disc pl-6 text-red-800 space-y-1">
            <li>Antioxidants like NAC can be a "double-edged sword."</li>
            <li>May protect cancer cells, potentially diminishing chemo/radio efficacy or accelerating growth in some contexts</li>
            <li>Rule: Avoid high-dose NAC in active malignancy unless coordinated with oncologist</li>
            <li>Use with extreme caution if high cancer risk/precancerous lesions</li>
          </ul>
        </div>
        
        <div className="mb-6">
          <h4 className="font-semibold mb-2 flex items-center text-amber-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Use with Caution / Monitor:
          </h4>
          <ul className="text-sm list-disc pl-6 space-y-1">
            <li>Severe Asthma/Hyperresponsive Airways: Rare bronchospasm risk (oral); start low</li>
            <li>Sulfur/Sulfite Sensitivity: May worsen symptoms (headache, rash, fog). Start very low, support detox pathways (molybdenum, B6)</li>
            <li>Pregnancy/Breastfeeding: Category B (used for APAP OD). Chronic use: case-by-case, justify need. Likely safe in breastfeeding (low doses)</li>
          </ul>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Medication Interactions:</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Nitroglycerin (potentiates vasodilation &rarr; headache/dizziness)</li>
              <li>Potential with some chemo, immunosuppressants</li>
              <li>Insulin/hypoglycemics (may lower blood sugar slightly)</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Therapeutic Sequencing:</h4>
            <p className="text-sm">Often best after initial gut/nutritional groundwork. "Open drainage pathways" first.</p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 16,
    title: "Maximizing Success: Practical Clinical Pearls",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Maximizing Success: Practical Clinical Pearls</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex">
            <div className="mr-3 text-yellow-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9.663 17h4.673M12 12v5m-8.5-5a8.5 8.5 0 1117 0 8.5 8.5 0 01-17 0zm0 0h17" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Hydration is Key</h4>
              <p className="text-sm">Encourage adequate water intake (metabolite excretion)</p>
            </div>
          </div>
          
          <div className="flex">
            <div className="mr-3 text-yellow-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Timing Matters</h4>
              <ul className="text-sm list-disc pl-5">
                <li>Morning/afternoon if energizing</li>
                <li>Evening for overnight detox</li>
                <li>Take with a little carb if GI upset on empty stomach</li>
              </ul>
            </div>
          </div>
          
          <div className="flex">
            <div className="mr-3 text-yellow-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.121 15.536c-1.171 1.172-3.071 1.172-4.242 0-1.172-1.171-1.172-3.071 0-4.242 1.171-1.172 3.071-1.172 4.242 0M19.39 17.75c-2.343 2.343-6.142 2.343-8.485 0-2.343-2.343-2.343-6.142 0-8.485 2.343-2.343 6.142-2.343 8.485 0" />
                <path d="M8.5 11.5l7 7" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold mb-1">"Rotten Egg Smell"</h4>
              <ul className="text-sm list-disc pl-5">
                <li>Normal (sulfur content). Quality matters</li>
                <li>Store cool/dry. Mix powder in citrus juice</li>
              </ul>
            </div>
          </div>
          
          <div className="flex">
            <div className="mr-3 text-yellow-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 10V8a7 7 0 1114 0v2M19 10v12H5V10M15 14a1 1 0 01-1 1h-4a1 1 0 01-1-1M8 10v-2h8v2" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Start Low in Sensitive Individuals</h4>
              <ul className="text-sm list-disc pl-5">
                <li>Anxiety, multiple chemical sensitivities: start 100-250mg/day</li>
                <li>Consider pairing with Vitamin C initially</li>
              </ul>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 17,
    title: "More Pearls & Protocol Integration",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">More Pearls & Protocol Integration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Monitor Methylation & Minerals</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Check homocysteine (add B vitamins if needed)</li>
              <li>Ensure adequate trace minerals (Zn, Cu, Se) with long-term/high-dose NAC</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">NAC & Energy/Exercise</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Can improve performance/recovery</li>
              <li>Advise taking away from immediate workout window (hormesis concept)</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Long-Term Compliance</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Educate on tangible & "silent" benefits</li>
              <li>Show lab improvements</li>
              <li>Simplify regimens (combo products)</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Holistic Approach</h4>
            <p className="text-sm">Integrate with diet (cysteine/sulfur-rich foods), exercise, sleep, stress management</p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold mb-2">Patient Education</h4>
          <p className="text-sm">
            NAC is well-established (mucolytic since 1960s, APAP antidote since 1980s). FDA-approved drug, OTC supplement.
          </p>
          
          <div className="mt-4 flex justify-center">
            <div className="w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="45" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
                <g transform="translate(50, 50)">
                  <g transform="rotate(0)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">NAC</text>
                  </g>
                  <g transform="rotate(60)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">Diet</text>
                  </g>
                  <g transform="rotate(120)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">Sleep</text>
                  </g>
                  <g transform="rotate(180)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">Exercise</text>
                  </g>
                  <g transform="rotate(240)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">Stress</text>
                  </g>
                  <g transform="rotate(300)">
                    <line x1="0" y1="0" x2="0" y2="-30" stroke="#4b5563" strokeWidth="2" />
                    <text x="0" y="-35" textAnchor="middle" fontSize="8" fill="#4b5563">Supplements</text>
                  </g>
                  <circle cx="0" cy="0" r="8" fill="#3b82f6" />
                  <text x="0" y="3" textAnchor="middle" fontSize="8" fill="white">Health</text>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 18,
    title: "Integrating NAC: Example Sequences",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Integrating NAC: Example Sequences</h3>
        
        <div className="space-y-6">
          <div className="bg-blue-50 p-5 rounded-lg">
            <h4 className="font-semibold mb-3">Example 1: "Gut-Liver Detox Sequence"</h4>
            <p className="text-sm italic mb-3">Patient with bloating, high LFTs, fatigue</p>
            
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center">
                <div className="px-3 py-2 bg-white rounded-lg border border-blue-200 text-sm">
                  <div className="font-medium">Month 1</div>
                  <div>Gut Focus</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <div className="px-3 py-2 bg-white rounded-lg border border-blue-200 text-sm">
                  <div className="font-medium">Month 2</div>
                  <div>Detox Focus</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <div className="px-3 py-2 bg-white rounded-lg border border-blue-200 text-sm">
                  <div className="font-medium">Month 3</div>
                  <div>Rebuilding</div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="p-3 bg-white rounded border border-blue-100 text-sm">
                <div className="font-medium mb-1">Month 1 (Gut Focus)</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Diet, probiotics, antimicrobials</li>
                  <li>Light NAC (600mg) for biofilm</li>
                </ul>
              </div>
              
              <div className="p-3 bg-white rounded border border-blue-100 text-sm">
                <div className="font-medium mb-1">Month 2 (Detox Focus)</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Increase NAC (1,200-1,800mg)</li>
                  <li>Add milk thistle, fiber</li>
                </ul>
              </div>
              
              <div className="p-3 bg-white rounded border border-blue-100 text-sm">
                <div className="font-medium mb-1">Month 3 (Rebuilding)</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Continue NAC</li>
                  <li>Add Glycine, multivitamin, exercise</li>
                </ul>
              </div>
            </div>
            
            <div className="text-xs bg-blue-100 p-2 rounded">
              <strong>Rationale:</strong> Prepare gut, then support detox, then rebuild. Prevents detox reactions.
            </div>
          </div>
          
          <div className="bg-green-50 p-5 rounded-lg">
            <h4 className="font-semibold mb-3">Example 2: "Neuroimmune Sequence"</h4>
            <p className="text-sm italic mb-3">Patient with brain fog, joint pain, positive ANA</p>
            
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center">
                <div className="px-3 py-2 bg-white rounded-lg border border-green-200 text-sm">
                  <div className="font-medium">Phase 1</div>
                  <div>Inflammation Reduction</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <div className="px-3 py-2 bg-white rounded-lg border border-green-200 text-sm">
                  <div className="font-medium">Phase 2</div>
                  <div>Immune Modulation</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <div className="px-3 py-2 bg-white rounded-lg border border-green-200 text-sm">
                  <div className="font-medium">Phase 3</div>
                  <div>Maintenance</div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="p-3 bg-white rounded border border-green-100 text-sm">
                <div className="font-medium mb-1">Phase 1</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Diet, fish oil, curcumin</li>
                </ul>
              </div>
              
              <div className="p-3 bg-white rounded border border-green-100 text-sm">
                <div className="font-medium mb-1">Phase 2</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Introduce NAC (600mg initially, titrate to ~2g/day)</li>
                  <li>Add selenium, B-vitamins</li>
                </ul>
              </div>
              
              <div className="p-3 bg-white rounded border border-green-100 text-sm">
                <div className="font-medium mb-1">Phase 3</div>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>Continue NAC</li>
                  <li>Integrate exercise/sauna</li>
                </ul>
              </div>
            </div>
            
            <div className="text-xs bg-green-100 p-2 rounded">
              <strong>Rationale:</strong> Address acute inflammation first, then use NAC to bolster defenses.
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 19,
    title: "Case Study 1: John, 52M - NAFLD & Metabolic Syndrome",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Case Study 1: John, 52M - NAFLD & Metabolic Syndrome</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Presentation</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Fatigue, central obesity</li>
              <li>ALT 65 U/L, AST 48 U/L</li>
              <li>Fatty liver (US)</li>
              <li>FBG 110 mg/dL</li>
              <li>CRP 4.5 mg/L</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Intervention</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Dietary changes (low sugar, Mediterranean)</li>
              <li>Walking program</li>
              <li>NAC 600 mg BID</li>
              <li>B-complex, Vitamin E</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-5">
          <h4 className="font-semibold mb-3">Outcome (6 Months)</h4>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">ALT:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">65 U/L</span>
                  <span className="text-sm text-green-600 font-medium">35 U/L</span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">AST:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">48 U/L</span>
                  <span className="text-sm text-green-600 font-medium">30 U/L</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Weight:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">+0 lbs</span>
                  <span className="text-sm text-green-600 font-medium">-10 lbs</span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">CRP:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">4.5 mg/L</span>
                  <span className="text-sm text-green-600 font-medium">1.2 mg/L</span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Energy:</span>
                <span className="text-sm text-green-600 font-medium">Increased</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Liver Tenderness:</span>
                <span className="text-sm text-green-600 font-medium">Reduced</span>
              </div>
            </div>
          </div>
          <div className="mt-2 text-sm">
            Modest decrease in hepatic fat (US)
          </div>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">NAC's Role</h4>
          <ul className="text-sm list-disc pl-5 space-y-1">
            <li>Bolstered liver antioxidant defenses</li>
            <li>Supported fat clearance</li>
            <li>Improved insulin sensitivity</li>
            <li>Reduced inflammation</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 20,
    title: "Case Study 2: Maria, 34F - Ulcerative Colitis Maintenance",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">Case Study 2: Maria, 34F - Ulcerative Colitis Maintenance</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Presentation</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>UC in remission on Mesalamine</li>
              <li>Keen to stay off steroids</li>
              <li>Baseline fecal calprotectin 150 µg/g (mildly elevated)</li>
              <li>CRP 6 mg/L</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Intervention</h4>
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li>Continued Mesalamine</li>
              <li>NAC 500 mg BID</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-5">
          <h4 className="font-semibold mb-3">Outcome (3 Months)</h4>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Fecal Calprotectin:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">150 µg/g</span>
                  <span className="text-sm text-green-600 font-medium">60 µg/g</span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">CRP:</span>
                <div className="flex items-center">
                  <span className="text-sm line-through text-gray-500 mr-2">6 mg/L</span>
                  <span className="text-sm text-green-600 font-medium">2 mg/L</span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Clinical Remission:</span>
                <span className="text-sm text-green-600 font-medium">Maintained, no flares</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">GI Symptoms:</span>
                <span className="text-sm text-green-600 font-medium">Less bloating, more formed stools</span>
              </div>
            </div>
          </div>
          <div className="mt-2 text-sm">
            Colonoscopy (1 year): Healthy mucosa, minor residual inflammation
          </div>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">NAC's Role</h4>
          <ul className="text-sm list-disc pl-5 space-y-1">
            <li>Scavenged mucosal free radicals</li>
            <li>Inhibited pro-inflammatory cytokines</li>
            <li>Maintained mucosal healing</li>
            <li>Synergized with mesalamine</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 21,
    title: "NAC: A Cornerstone in Longevity & Preventive Medicine",
    content: (
      <>
        <h3 className="text-lg font-bold mb-4">NAC: A Cornerstone in Longevity & Preventive Medicine</h3>
        
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="md:w-2/3">
            <div className="bg-blue-50 p-5 rounded-lg">
              <h4 className="font-semibold mb-3">NAC is a powerful, multifaceted compound:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Replenishes glutathione, quells oxidative stress</li>
                <li>Protects mitochondria, aids detoxification, calms inflammation</li>
                <li>Versatile dosing and synergistic potential (esp. GlyNAC)</li>
                <li>Key biomarkers help guide therapy and demonstrate efficacy</li>
                <li className="text-red-600 font-medium">Critical Caution: Active cancer</li>
              </ul>
              
              <div className="mt-4 space-y-2">
                <p>NAC empowers the body's endogenous healing and repair mechanisms.</p>
                <p>A relatively affordable, widely available, and generally safe intervention.</p>
                <p className="font-serif italic">"NAC is like giving your cells a better toolkit to deal with stress and time."</p>
              </div>
            </div>
          </div>
          
          <div className="md:w-1/3">
            <div className="bg-gray-50 p-5 rounded-lg h-full flex items-center justify-center">
              <div className="w-32 h-32">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle cx="50" cy="50" r="45" fill="#e5edff" stroke="#3b82f6" strokeWidth="2" />
                  <g transform="translate(50, 50)">
                    <circle cx="0" cy="0" r="30" fill="#f3f4f6" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="5,3" />
                    <circle cx="0" cy="0" r="15" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
                    <rect x="-10" y="-10" width="20" height="20" fill="#93c5fd" rx="3" />
                    <text x="0" y="2" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1e40af">NAC</text>
                  </g>
                  <g transform="translate(50, 20)">
                    <text x="0" y="0" textAnchor="middle" fontSize="8" fill="#3b82f6">Cellular</text>
                    <text x="0" y="10" textAnchor="middle" fontSize="8" fill="#3b82f6">Protection</text>
                  </g>
                  <g transform="translate(80, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="8" fill="#3b82f6">Reduce</text>
                    <text x="0" y="10" textAnchor="middle" fontSize="8" fill="#3b82f6">ROS</text>
                  </g>
                  <g transform="translate(50, 80)">
                    <text x="0" y="0" textAnchor="middle" fontSize="8" fill="#3b82f6">Detox</text>
                    <text x="0" y="10" textAnchor="middle" fontSize="8" fill="#3b82f6">Support</text>
                  </g>
                  <g transform="translate(20, 50)">
                    <text x="0" y="0" textAnchor="middle" fontSize="8" fill="#3b82f6">Anti-</text>
                    <text x="0" y="10" textAnchor="middle" fontSize="8" fill="#3b82f6">Inflammatory</text>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
  },
];

export default NACClinicalApplication;